// KPIs de la série de poids : tendance, lissage, volatilité, ETA, progression, plateau, paliers.

import { dayKey, daysBetween, toDate } from './datetime';
import {
  clamp,
  coefficientOfVariation,
  deltas,
  exponentialMovingAverage,
  iqr,
  linearRegression,
  mean,
  median,
  movingAverage,
  percentile,
  range,
  round,
  stddev,
} from './statistics';
import type { Milestone, WeightEntry } from './types';

export type SmoothingMethod = 'sma' | 'ewma';

interface IndexedPoint {
  dayIndex: number; // jours écoulés depuis la 1re pesée
  date: string;
  weightKg: number;
}

function toIndexed(entries: readonly WeightEntry[]): IndexedPoint[] {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length === 0) return [];
  const origin = sorted[0]!.date;
  return sorted.map((e) => ({
    dayIndex: daysBetween(origin, e.date),
    date: e.date,
    weightKg: e.weightKg,
  }));
}

export interface WeightTrend {
  slopePerDay: number; // kg/jour, négative = perte
  slopePerWeek: number; // kg/semaine
  r2: number; // qualité de l'ajustement, 0–1
}

// Tendance linéaire du poids (OLS sur l'axe temporel réel).
export function weightTrend(entries: readonly WeightEntry[]): WeightTrend {
  const pts = toIndexed(entries);
  // n<2 → 0 (série neutre) plutôt que NaN, pour ne pas propager NaN dans l'UI.
  if (pts.length < 2) return { slopePerDay: 0, slopePerWeek: 0, r2: 0 };
  const reg = linearRegression(pts.map((p) => ({ x: p.dayIndex, y: p.weightKg })));
  return {
    slopePerDay: reg.slope,
    slopePerWeek: reg.slope * 7,
    r2: reg.r2,
  };
}

// Lissage de la courbe (atténue le bruit eau/glycogène). 'sma' (défaut) ou 'ewma' (alpha = 2/(window+1)).
// window compte des PESÉES consécutives, pas des jours calendaires : exact si cadence quasi quotidienne.
export function smoothedWeight(
  entries: readonly WeightEntry[],
  window = 7,
  method: SmoothingMethod = 'sma',
): { date: string; weightKg: number; smoothed: number }[] {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const values = sorted.map((e) => e.weightKg);
  const smoothed =
    method === 'ewma'
      ? exponentialMovingAverage(values, 2 / (window + 1))
      : movingAverage(values, window);
  return sorted.map((e, i) => ({
    date: e.date,
    weightKg: e.weightKg,
    smoothed: round(smoothed[i]!, 2),
  }));
}

export interface WeightDistribution {
  p25: number;
  median: number;
  p75: number;
  iqr: number;
  cv: number; // coefficient de variation
}

export function weightDistribution(entries: readonly WeightEntry[]): WeightDistribution {
  const values = entries.map((e) => e.weightKg);
  return {
    p25: round(percentile(values, 25), 2),
    median: round(median(values), 2),
    p75: round(percentile(values, 75), 2),
    iqr: round(iqr(values), 2),
    cv: round(coefficientOfVariation(values), 4),
  };
}

// Poids courant robuste : moyenne des `window` dernières pesées (amortit une pesée aberrante).
export function currentWeight(entries: readonly WeightEntry[], window = 3): number {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length === 0) return NaN;
  const tail = sorted.slice(-window).map((e) => e.weightKg);
  return round(mean(tail), 2);
}

export interface ProgressStats {
  startKg: number;
  currentKg: number;
  targetKg: number;
  idealKg: number;
  lostKg: number; // positif = perte
  toTargetKg: number;
  toIdealKg: number;
  targetProgressPct: number; // borné [0, 100]
  idealProgressPct: number; // borné [0, 100]
}

export function progress(
  entries: readonly WeightEntry[],
  targetKg: number,
  idealKg: number,
): ProgressStats {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const startKg = sorted.length ? sorted[0]!.weightKg : NaN;
  const currentKg = currentWeight(entries);

  // Progression directionnelle : fonctionne que la cible soit sous (perte) ou
  // au-dessus (prise de masse) du poids de départ. `total` signé start→goal.
  const pct = (goal: number) => {
    const total = goal - startKg;
    if (total === 0) return currentKg === goal ? 100 : 0;
    return round(clamp(((currentKg - startKg) / total) * 100, 0, 100), 1);
  };
  // Distance restante (toujours ≥ 0), quel que soit le sens.
  const toGoal = (goal: number) => round(Math.max(0, Math.abs(goal - currentKg)), 2);

  return {
    startKg: round(startKg, 2),
    currentKg,
    targetKg,
    idealKg,
    lostKg: round(startKg - currentKg, 2), // négatif = pris (prise de masse)
    toTargetKg: toGoal(targetKg),
    toIdealKg: toGoal(idealKg),
    targetProgressPct: pct(targetKg),
    idealProgressPct: pct(idealKg),
  };
}

// ETA : date estimée d'atteinte de la cible en extrapolant la tendance. Directionnel :
// perte (cible plus basse) ↔ pente négative, prise (cible plus haute) ↔ pente positive.
// null si la tendance s'éloigne de la cible (ou est plate/indéfinie).
export function projectReachDate(
  entries: readonly WeightEntry[],
  targetKg: number,
): { days: number; date: string } | null {
  const pts = toIndexed(entries);
  if (pts.length < 2) return null;
  const { slopePerDay } = weightTrend(entries);
  const current = currentWeight(entries);
  if (!Number.isFinite(slopePerDay) || slopePerDay === 0) return null;
  const gap = targetKg - current; // >0 : cible au-dessus (prise) ; <0 : en dessous (perte)
  if (gap === 0) return { days: 0, date: pts[pts.length - 1]!.date };
  if (Math.sign(gap) !== Math.sign(slopePerDay)) return null; // on s'éloigne de la cible

  const days = Math.ceil(gap / slopePerDay); // mêmes signes → > 0
  const lastDate = toDate(pts[pts.length - 1]!.date);
  const eta = new Date(lastDate);
  eta.setDate(eta.getDate() + days);
  // dayKey (local) et non toISOString (UTC) : évite un décalage d'un jour.
  return { days, date: dayKey(eta) };
}

export interface VolatilityStats {
  stddevKg: number; // dispersion brute des pesées
  rangeKg: number; // amplitude max-min
  dailyNoiseKg: number; // écart-type des variations jour-à-jour (bruit)
}

export function volatility(entries: readonly WeightEntry[]): VolatilityStats {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const weights = sorted.map((e) => e.weightKg);
  // n<2 → 0 (ni écart-type ni bruit définis), pas NaN.
  if (weights.length < 2) return { stddevKg: 0, rangeKg: 0, dailyNoiseKg: 0 };
  return {
    stddevKg: round(stddev(weights, true), 3),
    rangeKg: round(range(weights), 2),
    dailyNoiseKg: round(stddev(deltas(weights), true), 3),
  };
}

// Plateau : sur les `windowDays` derniers jours, |pente hebdo| < seuil.
export function isPlateau(
  entries: readonly WeightEntry[],
  windowDays = 21,
  weeklyThresholdKg = 0.15,
): boolean {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 3) return false;
  const lastDate = sorted[sorted.length - 1]!.date;
  const recent = sorted.filter((e) => daysBetween(e.date, lastDate) <= windowDays);
  if (recent.length < 3) return false;
  const { slopePerWeek } = weightTrend(recent);
  return Math.abs(slopePerWeek) < weeklyThresholdKg;
}

export type MilestoneProgress<M extends Milestone = Milestone> = M & {
  reached: boolean;
  remainingKg: number; // 0 si atteint
};

// Statut des paliers : atteint dès que le poids courant <= palier. Préserve les champs additionnels de M.
export function milestoneProgress<M extends Milestone>(
  entries: readonly WeightEntry[],
  milestones: readonly M[],
): MilestoneProgress<M>[] {
  const current = currentWeight(entries);
  return [...milestones]
    .sort((a, b) => b.targetWeightKg - a.targetWeightKg)
    .map((m) => {
      const reached = m.achievedAt != null || current <= m.targetWeightKg;
      return {
        ...m,
        reached,
        remainingKg: reached ? 0 : round(current - m.targetWeightKg, 2),
      };
    });
}
